import Popover, { PopoverOrigin } from "@mui/material/Popover";
import { styled } from "@mui/material/styles";
import React, { useEffect, useState } from "react";

import { DatagridFilter, DatagridFilterOptions } from "./types";
import { FilterContent } from "./FilterContent";
import { FilterLoading } from "./FilterLoading";
import { ReactComponent as FilterAppliedIcon } from "./icons/filter-applied.svg";
import { ReactComponent as FilterIcon } from "./icons/filter.svg";
import Badge from "@mui/material/Badge";

const FilterIconContainer = styled("div")({
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  marginTop: "6px",
  color: "#6750A4",
});

const DEFAULT_DROPDOWN_WIDTH = 420;

const FilterDropdown = styled("div")<{ dropdownWidth: number }>(
  ({ dropdownWidth }) => ({
    width: `${dropdownWidth}px`,
    maxHeight: "400px",
    padding: "16px",
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  })
);

export interface DropdownFilterTriggerProps {
  onOpen: () => void;
  onClearSelection: () => void;
  selectedCount: number;
  isOpen: boolean;
}

export interface DropdownFilterProps {
  options: DatagridFilterOptions;
  selectedValues: string[];
  onChange: (values: string[]) => void;
  hideFilterCount?: boolean;
  hideSearch?: boolean;
  getOptionDisabledReason?: (
    value: string,
    selectedValues: string[]
  ) => string | undefined;
  width?: number;
  renderTrigger?: (props: DropdownFilterTriggerProps) => React.ReactNode;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
}

const DEFAULT_ANCHOR_ORIGIN: PopoverOrigin = {
  vertical: "bottom",
  horizontal: "right",
};
const DEFAULT_TRANSFORM_ORIGIN: PopoverOrigin = {
  vertical: "top",
  horizontal: "right",
};

export const DropdownFilter: React.FC<DropdownFilterProps> = ({
  options,
  selectedValues,
  onChange,
  hideFilterCount,
  hideSearch,
  getOptionDisabledReason,
  width = DEFAULT_DROPDOWN_WIDTH,
  renderTrigger,
  anchorOrigin = DEFAULT_ANCHOR_ORIGIN,
  transformOrigin = DEFAULT_TRANSFORM_ORIGIN,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [tempSelectedValues, setTempSelectedValues] =
    useState<string[]>(selectedValues);
  const filterButtonRef = React.useRef<HTMLDivElement>(null);

  const [resolvedOptions, setResolvedOptions] = useState<
    DatagridFilter[] | null
  >(Array.isArray(options) ? options : null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (Array.isArray(options)) {
      setResolvedOptions(options);
      setLoading(false);
      return;
    }

    setLoading(true);
    let cancelled = false;

    options.then((result) => {
      if (cancelled) return;
      setResolvedOptions(result);
      setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [options]);

  const handleFilterClick = () => {
    setTempSelectedValues(selectedValues);
    setIsOpen(true);
  };

  const handleFilterClose = () => {
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleApply = () => {
    onChange(tempSelectedValues);
    handleFilterClose();
  };

  const handleClear = () => {
    setTempSelectedValues([]);
  };

  return (
    <>
      {renderTrigger ? (
        <div
          ref={filterButtonRef}
          style={{ display: "inline-flex" }}>
          {renderTrigger({
            onOpen: handleFilterClick,
            onClearSelection: () => onChange([]),
            selectedCount: selectedValues.length,
            isOpen,
          })}
        </div>
      ) : (
        <Badge
          badgeContent={selectedValues.length}
          sx={{
            "& .MuiBadge-badge": {
              color: "#ffffff",
              backgroundColor: "#6750A4",
            },
          }}>
          <FilterIconContainer
            ref={filterButtonRef}
            onClick={handleFilterClick}>
            {selectedValues.length > 0 ? <FilterAppliedIcon /> : <FilterIcon />}
          </FilterIconContainer>
        </Badge>
      )}

      <Popover
        open={isOpen}
        anchorEl={filterButtonRef.current}
        onClose={handleFilterClose}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}>
        <FilterDropdown dropdownWidth={width}>
          {loading || !resolvedOptions ? (
            <FilterLoading />
          ) : (
            <FilterContent
              options={resolvedOptions}
              selectedValues={tempSelectedValues}
              onSelectionChange={setTempSelectedValues}
              onApply={handleApply}
              onClear={handleClear}
              hideFilterCount={hideFilterCount}
              hideSearch={hideSearch}
              getOptionDisabledReason={getOptionDisabledReason}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
            />
          )}
        </FilterDropdown>
      </Popover>
    </>
  );
};
